import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Box, Chip, Link, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { useState } from 'react';
import type { PdlCapacityWaterfallModel, PdlWaterfallBar } from '../../lib/pdlCapacityWaterfall';
import {
  PDL_BINDING,
  PDL_WATERFALL_BANK_TOGGLE,
  PDL_WATERFALL_LAYER,
  PDL_WATERFALL_TITLE,
} from '../../lib/pdlAdjudicatorUiCopy';
import { PDL_CHIP_SX, PDL_SECTION_RADIUS, PDL_SUPPORT_LABEL_SX } from '../../lib/pdlDecisionChrome';

type LayerKey = PdlWaterfallBar['layer'];

const LAYER_ACCENT: Record<LayerKey, 'info' | 'primary' | 'success'> = {
  observation: 'info',
  policy: 'primary',
  decision: 'success',
};

function fmt(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function barWidthPct(amount: number, scaleMax: number): number {
  if (scaleMax <= 0) return 0;
  return Math.max(4, Math.min(100, (amount / scaleMax) * 100));
}

function barTone(
  bar: PdlWaterfallBar,
  theme: Theme,
): { fill: string; edge: string; text: string } {
  if (bar.kind === 'requested') {
    return {
      fill: alpha(theme.palette.info.main, 0.1),
      edge: alpha(theme.palette.info.main, 0.45),
      text: theme.palette.info.dark,
    };
  }
  if (bar.binding && bar.kind !== 'final_offer') {
    return {
      fill: alpha(theme.palette.warning.main, 0.22),
      edge: theme.palette.warning.main,
      text: theme.palette.warning.dark,
    };
  }
  if (bar.kind === 'final_offer') {
    return {
      fill: alpha(theme.palette.success.main, 0.2),
      edge: theme.palette.success.main,
      text: theme.palette.success.dark,
    };
  }
  return {
    fill: alpha(theme.palette.primary.main, 0.12),
    edge: alpha(theme.palette.primary.main, 0.45),
    text: theme.palette.text.primary,
  };
}

function LayerHeading({
  layer,
  compareMode,
}: {
  layer: LayerKey;
  compareMode?: boolean;
}) {
  const theme = useTheme();
  const accent = LAYER_ACCENT[layer];
  const meta =
    layer === 'policy' && compareMode ? PDL_WATERFALL_LAYER.policyCompare : PDL_WATERFALL_LAYER[layer];
  const palette = theme.palette[accent];

  return (
    <Box sx={{ mb: 0.65 }}>
      <Typography
        component="h4"
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          fontSize: '0.8125rem',
          lineHeight: 1.35,
          color: palette.dark,
          pl: 1,
          borderLeft: '3px solid',
          borderColor: palette.main,
        }}
      >
        {meta.label}
      </Typography>
      <Typography variant="caption" sx={{ ...PDL_SUPPORT_LABEL_SX, display: 'block', pl: 1, mt: 0.2 }}>
        {meta.hint}
      </Typography>
    </Box>
  );
}

function WaterfallBar3d({
  bar,
  scaleMax,
  reducedMotion,
}: {
  bar: PdlWaterfallBar;
  scaleMax: number;
  reducedMotion: boolean;
}) {
  const theme = useTheme();
  const tone = barTone(bar, theme);
  const widthPct = barWidthPct(bar.amount, scaleMax);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(148px, 36%) 1fr auto' },
        gap: { xs: 0.35, sm: 1.25 },
        alignItems: 'center',
        py: 0.65,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35, color: tone.text }}>
            {bar.label}
          </Typography>
          {bar.binding ? (
            <Chip
              size="small"
              icon={<FlagRoundedIcon sx={{ fontSize: '14px !important' }} />}
              label={PDL_BINDING.chip}
              color="warning"
              sx={{ ...PDL_CHIP_SX, fontWeight: 800, '& .MuiChip-label': { px: 0.75 } }}
            />
          ) : null}
        </Stack>
        {bar.sublabel ? (
          <Typography variant="caption" sx={{ ...PDL_SUPPORT_LABEL_SX, display: 'block' }}>
            {bar.sublabel}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          minWidth: 0,
          gridColumn: { xs: '1 / -1', sm: 'auto' },
          order: { xs: 3, sm: 0 },
        }}
      >
        <Box sx={{ position: 'relative', height: 26 }} aria-hidden>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.divider, 0.35),
            }}
          />
          <Box
            sx={{
              width: `${widthPct}%`,
              height: '100%',
              borderRadius: 1,
              bgcolor: tone.fill,
              border: '2px solid',
              borderColor: tone.edge,
              boxShadow: bar.binding
                ? `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.45)}`
                : `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.3)}`,
              transition: reducedMotion ? 'none' : 'width 0.35s ease',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          />
        </Box>
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: tone.text,
          textAlign: { xs: 'left', sm: 'right' },
          gridColumn: { xs: '1 / -1', sm: 'auto' },
          order: { xs: 2, sm: 0 },
        }}
      >
        {fmt(bar.amount)}
      </Typography>
    </Box>
  );
}

export function PdlCapacityWaterfallVisual({ model }: { model: PdlCapacityWaterfallModel }) {
  const theme = useTheme();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const layers: LayerKey[] = ['observation', 'policy', 'decision'];
  const bankBar = model.bars.find((b) => b.kind === 'bank_ceiling');
  const bankIsBinding = bankBar?.binding === true;
  const [bankExpanded, setBankExpanded] = useState(bankIsBinding);
  const showBankRow = bankIsBinding || bankExpanded;
  const displayBars = model.bars.filter((b) => b.kind !== 'bank_ceiling' || showBankRow);
  const displayScaleMax = Math.max(...displayBars.map((b) => b.amount), 100);
  const canToggleBank = Boolean(bankBar && model.showBankPath && !bankIsBinding);

  return (
    <Box
      data-testid="pdl-capacity-waterfall"
      data-tour-target="pdl-capacity-waterfall"
      sx={{
        position: 'relative',
        borderRadius: PDL_SECTION_RADIUS,
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.22),
        overflow: 'hidden',
        boxShadow: `0 4px 18px ${alpha(theme.palette.primary.main, 0.06)}`,
      }}
    >
      <Box
        sx={{
          px: 1.35,
          py: 1.15,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
          <TuneRoundedIcon sx={{ fontSize: 20, color: 'primary.main' }} aria-hidden />
          <Typography
            component="h3"
            variant="subtitle1"
            sx={{ fontWeight: 800, lineHeight: 1.3, color: 'primary.dark', letterSpacing: '-0.01em' }}
          >
            {PDL_WATERFALL_TITLE}
          </Typography>
        </Stack>
        {model.bindingHeadline ? (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              mt: 0.65,
              lineHeight: 1.45,
              color: 'warning.dark',
              pl: 1,
              borderLeft: '3px solid',
              borderColor: 'warning.main',
            }}
          >
            {model.bindingHeadline}
          </Typography>
        ) : null}
        {model.scoringChips.length > 0 ? (
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.85 }}>
            {model.scoringChips.map((chip) => (
              <Chip
                key={chip}
                size="small"
                variant="outlined"
                label={chip}
                sx={{
                  ...PDL_CHIP_SX,
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  color: 'primary.dark',
                }}
              />
            ))}
          </Stack>
        ) : null}
      </Box>

      <Box sx={{ px: 1.25, py: 1.15, bgcolor: 'background.paper' }}>
        {layers.map((layer) => {
          const layerBars = displayBars.filter((b) => b.layer === layer);
          if (layerBars.length === 0) return null;
          return (
            <Box key={layer} sx={{ mb: layer === 'decision' ? 0 : 1.35 }}>
              <LayerHeading layer={layer} compareMode={layer === 'policy' && showBankRow && model.showBankPath} />
              <Stack
                spacing={0.15}
                divider={<Box sx={{ borderBottom: '1px dashed', borderColor: 'divider', opacity: 0.45 }} />}
              >
                {layerBars.map((bar) => (
                  <WaterfallBar3d
                    key={bar.id}
                    bar={bar}
                    scaleMax={displayScaleMax}
                    reducedMotion={reducedMotion}
                  />
                ))}
              </Stack>
              {layer === 'policy' && canToggleBank ? (
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => setBankExpanded((v) => !v)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.35,
                    mt: 0.75,
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                  }}
                  data-testid="pdl-waterfall-bank-toggle"
                >
                  {bankExpanded ? PDL_WATERFALL_BANK_TOGGLE.hide : PDL_WATERFALL_BANK_TOGGLE.show}
                  {bankBar ? ` (${fmt(bankBar.amount)})` : ''}
                  <ExpandMoreRoundedIcon
                    sx={{
                      fontSize: 18,
                      transform: bankExpanded ? 'rotate(180deg)' : 'none',
                      transition: reducedMotion ? 'none' : 'transform 0.2s ease',
                    }}
                  />
                </Link>
              ) : null}
            </Box>
          );
        })}
      </Box>

      <Box
        component="table"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          p: 0,
          m: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
        aria-label={PDL_WATERFALL_TITLE}
      >
        <caption>Payday capacity limits compared</caption>
        <thead>
          <tr>
            <th scope="col">Step</th>
            <th scope="col">Amount</th>
          </tr>
        </thead>
        <tbody>
          {displayBars.map((bar) => (
            <tr key={bar.id}>
              <td>{bar.label}</td>
              <td>{fmt(bar.amount)}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}
